// Runtime data-binding client for the React smart-page runtime.
//
// Resolves a `PageDataSource` to rows/record at runtime, choosing the
// transport by surface:
//   - 'preview' (in-app / designer): authenticated preview-data fetch
//   - 'public'  (deployed site):     anonymous public-data fetch
//
// Deployment-time sources are NOT fetched here — they arrive baked in
// `page.__resolved_data__` and are seeded straight into `resources`.
//
// HTTP-shape coupling is intentionally pushed out via the `DataFetcher`
// adapter so the designer (which uses axios + cookies) and the standalone
// runtime (which uses fetch + API key or session) can each supply their
// own. The package doesn't import axios or any URL strings.

import type { PageDataSource, ResolvedDataEntry } from '@twasta/page-schema'

export type DataAccess =
  | { mode: 'preview'; project: string }
  | { mode: 'public'; publicKey: string; pageObjName: string }
  // In-app Site preview: end-user-scoped fetch that enforces the project's
  // object/action grants (data-level authz), carrying the visitor's session.
  // `embedToken` is the server-minted, page-bound token for THIS embed block
  // (external pages / anonymous visitors); the embed client sends it on every
  // site-txn / site-doc call. `onEmbedTokenExpired` re-fetches the page model
  // to obtain a fresh token when a call 401s with EMBED_TOKEN_EXPIRED.
  | {
      mode: 'site'
      userId: string
      project: string
      embedToken?: string
      onEmbedTokenExpired?: () => Promise<string | null>
      // Called when a site-txn call is refused because the data belongs to
      // ACCOUNTS (SIGN_IN_REQUIRED / SESSION_EXPIRED / PROJECT_USER_REQUIRED);
      // the host swaps the embed for the shared sign-in card
      // (`signInPrompt.tsx`).
      onSignInRequired?: (refusal: { code: 'SIGN_IN_REQUIRED' | 'SESSION_EXPIRED' | 'PROJECT_USER_REQUIRED'; message: string }) => void
      // Resolved page_input/static values for THIS embed's scope filters,
      // keyed by filter id ({fid: {value}} / {fid: {from,to}}). A page-bound
      // (anonymous) edit-mode embed must echo these on every site-txn call so
      // the server can enforce the designer's row scope (list/execute/batch).
      embedFilterValues?: Record<string, unknown>
    }

/** Adapter the engine calls when it needs to fetch a live data source.
 *
 *  Implementations decide auth (cookie vs key vs anonymous), transport
 *  (axios vs fetch), and the actual URL path. The two methods correspond
 *  to the two `DataAccess` modes — implement just the one you need on a
 *  surface that only does one (the SSR prerenderer, for example, returns
 *  empty results and skips runtime sources entirely).
 */
export interface DataFetcher {
  preview(
    project: string,
    source: PageDataSource,
    args?: Record<string, unknown>,
    scopes?: Record<string, unknown>,
  ): Promise<{ data: ResolvedDataEntry }>

  public_(
    publicKey: string,
    pageObjName: string,
    dsId: string,
    args?: Record<string, unknown>,
    scopes?: Record<string, unknown>,
  ): Promise<{ data: ResolvedDataEntry }>

  /** In-app Site preview fetch — carries the end-user session so the server
   *  can enforce object/action grants. Optional: surfaces that don't render
   *  the Site preview need not implement it. */
  site?(
    userId: string,
    project: string,
    source: PageDataSource,
    args?: Record<string, unknown>,
    scopes?: Record<string, unknown>,
  ): Promise<{ data: ResolvedDataEntry }>
}

/** Seed the runtime `resources` map from baked deployment-time data. */
export function seedResources(
  resolved: Record<string, ResolvedDataEntry> | undefined,
): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  if (!resolved) return out
  for (const [id, entry] of Object.entries(resolved)) {
    out[`ds:${id}`] = entry
  }
  return out
}

/** Fetch one data source live. `args` carries page-input values referenced
 *  by filters; `scopes` carries enclosing-row values for correlated sources. */
export async function fetchDataSource(
  fetcher: DataFetcher,
  access: DataAccess,
  source: PageDataSource,
  args?: Record<string, unknown>,
  scopes?: Record<string, unknown>,
  opts?: { throwOnError?: boolean },
): Promise<ResolvedDataEntry> {
  try {
    let data: ResolvedDataEntry
    if (access.mode === 'public') {
      data = (await fetcher.public_(
        access.publicKey, access.pageObjName, source.id, args, scopes,
      )).data
    } else if (access.mode === 'site') {
      if (!fetcher.site) throw new Error('site fetcher not provided')
      data = (await fetcher.site(access.userId, access.project, source, args, scopes)).data
    } else {
      data = (await fetcher.preview(access.project, source, args, scopes)).data
    }
    // The server answers a failed read with an empty entry carrying `error`
    // (a page must never break over one binding). A live poll asked to keep
    // what it shows treats that as the failure it is.
    const failed = (data as { error?: unknown } | undefined)?.error
    if (opts?.throwOnError && failed) throw new Error(String(failed))
    return data
  } catch (err) {
    // A live poll keeps what it already shows when a read fails (a network
    // blip must not empty a kitchen screen and then re-announce every row).
    if (opts?.throwOnError) throw err
    // Never break a render over a failed binding — return an empty result.
    return source.mode === 'record'
      ? { mode: 'record', row: null }
      : { mode: 'list', rows: [], total: 0 }
  }
}

/** Extract rows from a resolved entry, honoring correlated (nested) shape. */
export function rowsFromEntry(
  entry: ResolvedDataEntry | undefined,
  correlationKey?: string,
): Record<string, unknown>[] {
  if (!entry) return []
  if (entry.correlated && entry.by) {
    return correlationKey != null ? (entry.by[correlationKey] || []) : []
  }
  return entry.rows || []
}
